import React, { useState } from 'react';
import {
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Container,
} from 'react-bootstrap';
import { useGetStoreStocksQuery, useUpdateStoreStockMutation } from '../../slices/storeStocksApiSlice';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import { toast } from 'react-toastify';

const StoreStockNoteSearchReplaceScreen = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [replacementText, setReplacementText] = useState('');
  const [previewResults, setPreviewResults] = useState([]);
  const [isReplacing, setIsReplacing] = useState(false);

  const { data: storeStocksData, isLoading, error } = useGetStoreStocksQuery();
  const [updateStoreStock] = useUpdateStoreStockMutation();

  // Preview matches in notes
  const handleSearch = () => {
    if (!searchTerm.trim()) return;

    const matches = storeStocksData?.storeStocks
      ?.filter((storeStock) =>
        storeStock.note?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .map((storeStock) => ({
        storeStockId: storeStock._id,
        date: storeStock.date,
        originalNote: storeStock.note,
      }));

    setPreviewResults(matches || []);
  };

  // Replace notes
  const handleReplace = async () => {
    if (!replacementText.trim()) return toast.error('Replacement text required');
    if (!searchTerm.trim()) return toast.error('Search term required');

    const affectedStoreStocks = storeStocksData?.storeStocks?.filter((storeStock) =>
      storeStock.note?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!affectedStoreStocks || affectedStoreStocks.length === 0) {
      return toast.info('No matching notes found');
    }

    setIsReplacing(true);

    try {
      for (const storeStock of affectedStoreStocks) {
        const updatedNote = storeStock.note.replace(
          new RegExp(searchTerm, 'gi'),
          replacementText
        );

        await updateStoreStock({
          storeStockId: storeStock._id,
          updatedStoreStock: {
            ...storeStock,
            note: updatedNote,
          },
        }).unwrap();
      }

      toast.success(`Replaced "${searchTerm}" with "${replacementText}" in notes`);
      setPreviewResults([]);
      setSearchTerm('');
      setReplacementText('');
    } catch (err) {
      toast.error(err?.data?.message || 'Update failed');
    } finally {
      setIsReplacing(false);
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col md={12}>
          <h1>Search & Replace in StoreStock Notes</h1>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={5}>
          <Form.Control
            type="text"
            placeholder="Search for text in notes"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md={5}>
          <Form.Control
            type="text"
            placeholder="Replacement text"
            value={replacementText}
            onChange={(e) => setReplacementText(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Button
            variant="primary"
            onClick={handleSearch}
            className="me-2"
            disabled={!searchTerm.trim() || isReplacing}
          >
            Preview Matches
          </Button>
          <Button
            variant="success"
            onClick={handleReplace}
            disabled={!replacementText.trim() || isReplacing}
          >
            Replace All
          </Button>
        </Col>
      </Row>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'Error loading storeStocks'}</Message>
      ) : previewResults.length === 0 ? (
        <Alert variant="info">No matching notes found. Try a different search term.</Alert>
      ) : (
        <Table striped bordered hover responsive className="table-sm">
          <thead>
            <tr>
              <th>DATE</th>
              <th>ORIGINAL NOTE</th>
            </tr>
          </thead>
          <tbody>
            {previewResults.map((item, idx) => (
              <tr key={idx}>
                <td>{new Date(item.date).toLocaleDateString()}</td>
                <td>{item.originalNote}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default StoreStockNoteSearchReplaceScreen;